import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { getUtil } from "@/constants/utilities";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Camera, ImagePlus, Trash2, X } from "lucide-react";

/**
 * Pin Details dialog — opens when a map pin is tapped. Lets tradies:
 *  - See the service, GPS, depth, label at a glance
 *  - Edit the label / depth / notes
 *  - Upload multiple photos from device (camera or gallery)
 *  - View uploaded photos as a gallery
 *  - Delete photos or the whole pin
 *
 * Photos are stored via the backend upload endpoint, which pushes them to the
 * Emergent Object Store and returns a stable storage path. We read images back
 * via the auth'd `/files/{path}?auth={jwt}` endpoint so <img> tags work.
 */
export const PinDetailsDialog = ({ open, onOpenChange, jobId, pin, onUpdatePin, onRemovePin }) => {
  const [uploading, setUploading] = useState(false);
  const [localPin, setLocalPin] = useState(pin);
  const fileInputRef = useRef(null);

  useEffect(() => { setLocalPin(pin); }, [pin]);

  if (!localPin) return null;
  const util = getUtil(localPin.service);
  const token = typeof window !== "undefined" ? localStorage.getItem("fp_token") : null;
  // Build the URL that <img> can consume — query-param auth (JWT) since <img>
  // can't send Authorization headers.
  const photoUrl = (path) => `${API}/files/${path}?auth=${encodeURIComponent(token || "")}`;

  const patchLocal = (patch) => {
    const next = { ...localPin, ...patch };
    setLocalPin(next);
    onUpdatePin(next);
  };

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/jobs/${jobId}/pins/${localPin.id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (r?.data?.path) uploaded.push(r.data.path);
      }
      if (uploaded.length) {
        const nextPhotos = [...(localPin.photos || []), ...uploaded];
        patchLocal({ photos: nextPhotos });
        toast.success(`${uploaded.length} photo${uploaded.length !== 1 ? "s" : ""} added`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Photo upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = async (path) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/jobs/${jobId}/pins/${localPin.id}/photos`, { params: { path } });
      const nextPhotos = (localPin.photos || []).filter((p) => p !== path);
      patchLocal({ photos: nextPhotos });
      toast.success("Photo removed");
    } catch (err) {
      toast.error("Could not remove photo");
    }
  };

  const removePin = () => {
    if (!window.confirm(`Remove ${util.label} pin?`)) return;
    onRemovePin(localPin.id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pin-details-dialog" className="max-w-2xl border-2 border-foreground rounded-none p-0 gap-0 z-[9999]">
        <DialogHeader className="border-b-2 border-foreground p-4 flex-row items-start gap-3 space-y-0">
          <span className="w-5 h-5 border-2 border-foreground shrink-0 mt-1" style={{ background: util.color }} />
          <div className="min-w-0 flex-1">
            <DialogTitle className="font-heading font-black uppercase text-2xl leading-tight">
              {util.label} pin
            </DialogTitle>
            <DialogDescription className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              {localPin.lat.toFixed(6)}, {localPin.lng.toFixed(6)}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Editable fields */}
          <div className="grid grid-cols-2 gap-3">
            <label className="font-mono text-[10px] uppercase tracking-widest">
              Label
              <input
                data-testid="pin-label-input"
                type="text"
                value={localPin.label || ""}
                onChange={(e) => patchLocal({ label: e.target.value })}
                placeholder="e.g. WM, iso valve"
                className="w-full mt-1 px-2 py-1.5 border-2 border-foreground bg-background font-mono text-xs"
              />
            </label>
            <label className="font-mono text-[10px] uppercase tracking-widest">
              Depth (mm)
              <input
                data-testid="pin-depth-input"
                type="number"
                value={localPin.depth_mm ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  patchLocal({ depth_mm: v === "" ? null : Number(v) });
                }}
                placeholder="400"
                className="w-full mt-1 px-2 py-1.5 border-2 border-foreground bg-background font-mono text-xs"
              />
            </label>
          </div>
          <label className="font-mono text-[10px] uppercase tracking-widest block">
            Notes
            <textarea
              data-testid="pin-notes-input"
              rows={2}
              value={localPin.notes || ""}
              onChange={(e) => patchLocal({ notes: e.target.value })}
              placeholder="Anything the client needs to know…"
              className="w-full mt-1 px-2 py-1.5 border-2 border-foreground bg-background font-mono text-xs"
            />
          </label>

          {/* Photo gallery */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-heading font-black uppercase text-sm tracking-widest">
                Photos <span className="font-mono text-xs text-muted-foreground">({(localPin.photos || []).length})</span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="pin-photo-camera-btn"
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.setAttribute("capture", "environment");
                      fileInputRef.current.click();
                    }
                  }}
                  disabled={uploading}
                  className="btn-ghost-rugged text-[11px] inline-flex items-center gap-1.5"
                >
                  <Camera className="w-3.5 h-3.5" /> Take
                </button>
                <button
                  type="button"
                  data-testid="pin-photo-upload-btn"
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.removeAttribute("capture");
                      fileInputRef.current.click();
                    }
                  }}
                  disabled={uploading}
                  className="btn-ghost-rugged text-[11px] inline-flex items-center gap-1.5"
                >
                  <ImagePlus className="w-3.5 h-3.5" /> Upload
                </button>
                <input
                  ref={fileInputRef}
                  data-testid="pin-photo-input"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onFilesPicked}
                  className="hidden"
                />
              </div>
            </div>

            {(localPin.photos || []).length === 0 ? (
              <div className="border-2 border-dashed border-foreground/40 p-6 text-center font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                {uploading ? "Uploading…" : "No photos yet — snap or upload site pics for the report."}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(localPin.photos || []).map((path) => (
                  <div key={path} data-testid="pin-photo-item"
                    className="relative aspect-square border-2 border-foreground overflow-hidden bg-muted">
                    <img
                      src={photoUrl(path)}
                      alt="Pin"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <button
                      type="button"
                      data-testid="pin-photo-remove"
                      onClick={() => removePhoto(path)}
                      className="absolute top-1 right-1 border-2 border-foreground bg-background p-1 hover:bg-destructive hover:text-background transition-colors"
                      aria-label="Remove photo"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {uploading && (
                  <div className="aspect-square border-2 border-dashed border-foreground/40 grid place-items-center font-mono text-[10px] uppercase tracking-widest">
                    Uploading…
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t-2 border-foreground p-3 flex-row items-center justify-between gap-2 sm:justify-between">
          <button
            type="button"
            data-testid="pin-remove-btn"
            onClick={removePin}
            className="btn-ghost-rugged text-[11px] inline-flex items-center gap-1.5 text-destructive border-destructive"
          >
            <Trash2 className="w-3.5 h-3.5" /> Remove pin
          </button>
          <button
            type="button"
            data-testid="pin-done-btn"
            onClick={() => onOpenChange(false)}
            className="btn-rugged text-[11px]"
          >
            Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PinDetailsDialog;
